import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { MANAGE } from '@/constants/testIds';
import { toast } from 'sonner';
import { Copy, ExternalLink, RefreshCw, Radar } from 'lucide-react';
import MilestoneCard from '@/components/MilestoneCard';
import ReportShareCard from '@/components/ReportShareCard';

const STATUS_STYLES = {
  active:   { label: 'ACTIVE',   cls: 'c-badge c-badge--active' },
  trialing: { label: 'TRIAL',    cls: 'c-badge c-badge--active' },
  past_due: { label: 'PAST_DUE', cls: 'c-badge c-badge--warn' },
  unpaid:   { label: 'UNPAID',   cls: 'c-badge c-badge--warn' },
  canceled: { label: 'CANCELED', cls: 'c-badge c-badge--danger' },
  inactive: { label: 'INACTIVE', cls: 'c-badge' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.inactive;
  return <span data-testid={MANAGE.subscriptionBadge} className={s.cls}>{s.label}</span>;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().slice(0, 10).replace(/-/g, '.');
  } catch {
    return iso;
  }
};

export const ProManagePage = () => {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [refData, setRefData] = useState(null);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadReferrals = useCallback(async () => {
    setLoadingRefs(true);
    try {
      const { data } = await api.get('/referrals');
      setRefData(data);
    } catch {
      toast.error('Failed to load referrals');
    } finally {
      setLoadingRefs(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadReferrals();
  }, [user, loadReferrals]);

  if (!user) return null;

  const referralLink = `${window.location.origin}/signup?ref=${user.referral_code}`;
  const hasSubscription = user.subscription_status !== 'inactive';

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post('/customer-portal', {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Portal unavailable');
      setPortalBusy(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} COPIED`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const doRefresh = async () => {
    setRefreshing(true);
    await Promise.all([refresh(), loadReferrals()]);
    setRefreshing(false);
  };

  return (
    <div
      data-testid={MANAGE.container}
      className="max-w-6xl mx-auto px-6 md:px-8 pt-10 pb-24"
    >
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
            // OPERATOR_CONTROL_ROOM
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter">
            /pro/manage
          </h1>
          <div className="text-[10px] text-gray-600 tracking-widest uppercase mt-2">
            OP::{user.email}
          </div>
        </div>
        <button
          onClick={doRefresh}
          className="c-btn-secondary text-xs inline-flex items-center gap-2"
          disabled={refreshing}
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={2.5} />
          SYNC
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Subscription Status */}
        <div
          data-testid={MANAGE.subscriptionStatus}
          className="c-card col-span-1 lg:col-span-2"
        >
          <div className="terminal-header flex items-center justify-between border-b border-[#3F3F46] pb-3 mb-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500">
              // SUBSCRIPTION_STATUS
            </div>
            <StatusBadge status={user.subscription_status} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <div>
              <div className="c-label">PLAN</div>
              <div data-testid={MANAGE.subscriptionPlan} className="text-white text-lg uppercase tracking-tight">
                {user.subscription_plan
                  ? user.subscription_plan === 'monthly' ? 'PRO · MONTHLY' : 'PRO · YEARLY'
                  : '—'}
              </div>
            </div>
            <div>
              <div className="c-label">NEXT_RENEWAL</div>
              <div data-testid={MANAGE.subscriptionRenewal} className="text-white text-lg">
                {formatDate(user.subscription_current_period_end)}
              </div>
            </div>
            <div>
              <div className="c-label">CREDITS_BALANCE</div>
              <div className="text-[#38BDF8] text-lg">
                {user.credits_pending_months} {user.credits_pending_months === 1 ? 'MONTH' : 'MONTHS'}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 pt-4 border-t border-[#3F3F46]">
            {hasSubscription ? (
              <>
                <button
                  data-testid={MANAGE.manageBillingBtn}
                  onClick={openPortal}
                  disabled={portalBusy || !user.stripe_customer_id}
                  className="c-btn-primary text-xs inline-flex items-center gap-2"
                >
                  MANAGE_BILLING <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
                </button>
                <Link to="/pro/inspect" className="c-btn-secondary text-xs inline-flex items-center gap-2">
                  <Radar className="w-3 h-3" strokeWidth={2.5} /> OPEN_INSPECTOR
                </Link>
                <Link to="/pro/watchlist" className="c-btn-secondary text-xs inline-flex items-center gap-2">
                  <Radar className="w-3 h-3" strokeWidth={2.5} /> OPEN_WATCHLIST
                </Link>
                <Link to="/pro/signals" className="c-btn-secondary text-xs inline-flex items-center gap-2">
                  <Radar className="w-3 h-3" strokeWidth={2.5} /> OPEN_SIGNALS
                </Link>
              </>
            ) : (
              <button
                data-testid={MANAGE.upgradeBtn}
                onClick={() => nav('/pricing')}
                className="c-btn-primary text-xs"
              >
                UPGRADE_CLEARANCE
              </button>
            )}
            {user.stripe_customer_id && !hasSubscription && (
              <button
                data-testid={MANAGE.manageBillingBtn}
                onClick={openPortal}
                disabled={portalBusy}
                className="c-btn-secondary text-xs inline-flex items-center gap-2"
              >
                OPEN_BILLING_PORTAL <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>

        {/* Referral card */}
        <div data-testid={MANAGE.referralCard} className="c-card">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 border-b border-[#3F3F46] pb-3 mb-4">
            // REFERRAL_LINK
          </div>
          <div className="c-label">YOUR_CODE</div>
          <div className="flex items-center gap-2 mb-4">
            <code
              data-testid={MANAGE.referralCode}
              className="text-[#38BDF8] text-xl tracking-[0.15em]"
            >
              {user.referral_code}
            </code>
            <button
              onClick={() => copy(user.referral_code, 'CODE')}
              className="c-btn-secondary text-[10px] px-2 py-1 inline-flex items-center gap-1"
              aria-label="copy code"
            >
              <Copy className="w-3 h-3" />
            </button>
          </div>
          <div className="c-label">SHARE_LINK</div>
          <div className="flex items-stretch gap-2">
            <code
              data-testid={MANAGE.referralLink}
              className="flex-1 truncate bg-[#27272A] border border-[#3F3F46] px-2 py-2 text-xs text-gray-400"
            >
              {referralLink}
            </code>
            <button
              data-testid={MANAGE.copyReferralBtn}
              onClick={() => copy(referralLink, 'LINK')}
              className="c-btn-primary text-[10px] px-3 inline-flex items-center gap-1"
            >
              <Copy className="w-3 h-3" /> COPY
            </button>
          </div>
          <div className="text-[10px] text-gray-600 tracking-widest uppercase mt-4">
            &gt; you both get +1 month on their first paid upgrade
          </div>
        </div>

        {/* Operators referred + credits pending */}
        <div
          data-testid={MANAGE.operatorsCard}
          className="c-card col-span-1 lg:col-span-3"
        >
          <div className="terminal-header flex items-center justify-between border-b border-[#3F3F46] pb-3 mb-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500">
              // OPERATORS_REFERRED &nbsp;·&nbsp; CREDITS_PENDING
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <Metric label="OPERATORS_REFERRED" value={refData?.operators_referred ?? 0} testId={MANAGE.operatorsCount} />
            <Metric label="CREDITS_PENDING (MONTHS)" value={refData?.credits_pending ?? 0} testId={MANAGE.creditsPending} accent />
            <Metric label="CREDITS_AWARDED (MONTHS)" value={refData?.credits_awarded_total ?? 0} testId={MANAGE.creditsAwarded} />
          </div>

          <div className="border border-[#3F3F46] bg-black">
            <div className="grid grid-cols-[1.4fr_0.9fr_0.9fr_0.9fr] text-[10px] tracking-[0.2em] uppercase text-gray-500 px-4 py-2 border-b border-[#3F3F46]">
              <div>OPERATOR_ID</div>
              <div>ENROLLED</div>
              <div>STATUS</div>
              <div>CREDIT</div>
            </div>
            {loadingRefs ? (
              <div className="px-4 py-6 text-xs text-gray-500">
                LOADING<span className="caelix-dots"></span>
              </div>
            ) : refData?.referrals?.length ? (
              refData.referrals.map((r, i) => (
                <div
                  key={i}
                  data-testid={MANAGE.referralRow}
                  className="grid grid-cols-[1.4fr_0.9fr_0.9fr_0.9fr] items-center px-4 py-3 border-b border-[#3F3F46] last:border-0 text-xs hover:bg-[#27272A] transition-colors"
                >
                  <div className="font-mono text-white truncate">{r.referee_email_masked}</div>
                  <div className="font-mono text-gray-500">{formatDate(r.created_at)}</div>
                  <div>
                    <span className={r.status === 'paid' ? 'c-badge c-badge--active' : 'c-badge c-badge--warn'}>
                      {r.status.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    {r.credit_awarded ? (
                      <span className="text-[#38BDF8] font-mono">+1 MONTH</span>
                    ) : (
                      <span className="text-gray-600 font-mono">— (pending upgrade)</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div
                data-testid={MANAGE.emptyReferrals}
                className="px-4 py-8 text-xs text-gray-500"
              >
                &gt; no operators referred yet · share your link to earn free months
              </div>
            )}
          </div>

          {!hasSubscription && (
            <div className="mt-6 text-[10px] tracking-[0.2em] uppercase text-yellow-500/70">
              &gt; awarded credits will apply automatically to your next stripe invoice once you upgrade
            </div>
          )}
        </div>

        <MilestoneCard refData={refData} currentTier={user.milestone_tier} />
        <ReportShareCard />
      </div>
    </div>
  );
};

const Metric = ({ label, value, testId, accent }) => (
  <div>
    <div className="c-label">{label}</div>
    <div
      data-testid={testId}
      className={`text-3xl font-bold ${accent ? 'text-[#38BDF8]' : 'text-white'}`}
    >
      {String(value).padStart(2, '0')}
    </div>
  </div>
);

export default ProManagePage;
